import type { CRMUser, EnhancedDeal, ProjectOnboarding, ProjectTask, TaskGroup, TimberProject } from '../types';
import { getAssignedDesigner } from './dealDesignerArchitect';
import { mergeTasksForWorkQueueEligibility } from './archEngineerWorkQueueEligibility';
import { compareTimberProjectsByDealMap } from './projectNumberSort';
import { taskInvolvesUser } from './projectTaskUsers';
import { projectHasSilentQaViewer } from './qaProjectAccess';
import { isTaskDueDatePastLocalCalendar } from './taskSchedule';

export interface ArchitectProjectGroup {
  project: TimberProject;
  deal: EnhancedDeal | undefined;
  myTasks: ProjectTask[];
  actionCount: number;
  overdueCount: number;
}

function architectHasProjectTies(
  project: TimberProject,
  deal: EnhancedDeal | undefined,
  userId: string,
  projectTasks: ProjectTask[],
  taskGroups: TaskGroup[],
  projectOnboardings: ProjectOnboarding[],
  crmUsers: CRMUser[],
): boolean {
  if (!userId || !project.id) return false;

  if (projectHasSilentQaViewer(project, userId)) return true;

  if (getAssignedDesigner(deal, crmUsers).uid === userId) return true;

  // Project roster so architects who receive a file-comment mention can open the deep link.
  if (Array.isArray(project.assignedTo) && project.assignedTo.includes(userId)) return true;

  if (project.projectManager === userId) return true;

  const merged = mergeTasksForWorkQueueEligibility(project, projectTasks, taskGroups, projectOnboardings);
  return merged.some((t) => taskInvolvesUser(t, userId));
}

/** True when the architect is assigned designer, on the project roster, or has task involvement. */
export function architectIsInvolvedInProject(
  project: TimberProject,
  deal: EnhancedDeal | undefined,
  userId: string,
  projectTasks: ProjectTask[],
  taskGroups: TaskGroup[],
  projectOnboardings: ProjectOnboarding[],
  crmUsers: CRMUser[],
): boolean {
  if (project.status === 'completed') return false;
  return architectHasProjectTies(
    project,
    deal,
    userId,
    projectTasks,
    taskGroups,
    projectOnboardings,
    crmUsers,
  );
}

/**
 * Broader involvement check for PDF/file comment email deep links. No completed-project
 * exclusion, so someone @mentioned on a file can open the pinned comment.
 */
export function architectIsInvolvedInProjectForFileComment(
  project: TimberProject,
  deal: EnhancedDeal | undefined,
  userId: string,
  projectTasks: ProjectTask[],
  taskGroups: TaskGroup[],
  projectOnboardings: ProjectOnboarding[],
  crmUsers: CRMUser[],
): boolean {
  return architectHasProjectTies(
    project,
    deal,
    userId,
    projectTasks,
    taskGroups,
    projectOnboardings,
    crmUsers,
  );
}

/** Non-closed projects the architect is tied to, excluding ids already shown in the priority task list. */
export function buildArchitectInvolvementGroups(
  timberProjects: TimberProject[],
  enhancedDeals: EnhancedDeal[],
  projectTasks: ProjectTask[],
  projectOnboardings: ProjectOnboarding[],
  taskGroups: TaskGroup[],
  userId: string,
  crmUsers: CRMUser[],
  excludeProjectIds: ReadonlySet<string>,
): ArchitectProjectGroup[] {
  if (!userId) return [];

  const now = new Date();
  const dealsById = new Map(enhancedDeals.filter((d) => d.id).map((d) => [d.id!, d] as const));
  const groups: ArchitectProjectGroup[] = [];

  for (const project of timberProjects) {
    if (!project.id || excludeProjectIds.has(project.id)) continue;
    if (project.status === 'completed') continue;

    const deal = project.dealId ? dealsById.get(project.dealId) : undefined;
    if (!architectIsInvolvedInProject(project, deal, userId, projectTasks, taskGroups, projectOnboardings, crmUsers)) {
      continue;
    }

    const merged = mergeTasksForWorkQueueEligibility(project, projectTasks, taskGroups, projectOnboardings);
    const myTasks = merged.filter((t) => taskInvolvesUser(t, userId));

    const actionCount = myTasks.filter(
      (t) => t.actionRequired === userId && t.status !== 'completed',
    ).length;
    const overdueCount = myTasks.filter(
      (t) => t.status !== 'completed' && isTaskDueDatePastLocalCalendar(new Date(t.endDate), now),
    ).length;

    groups.push({ project, deal, myTasks, actionCount, overdueCount });
  }

  groups.sort((a, b) => compareTimberProjectsByDealMap(a.project, b.project, dealsById));
  return groups;
}
